import { resolveAssessmentImageBaseUrl } from '../pages/assessment/assessmentApiConfig';

/** Assessment / HR-upload candidates (students_upload table) */
export function isAssessmentStudent(user) {
  if (!user) return false;
  if (user.is_student === true || user.is_student === 1 || user.is_student === '1') {
    return true;
  }
  const roleId = Number(user.role_id ?? user.user_role_id ?? 0);
  return roleId === 15;
}

/** HR module admins — Test Inventory (not take-test screen) */
const ASSESSMENT_ADMIN_USER_IDS = [1, 121];

export function isAssessmentAdmin(user) {
  if (!user || isAssessmentStudent(user)) return false;
  const userId = Number(user.user_id);
  if (ASSESSMENT_ADMIN_USER_IDS.includes(userId)) return true;
  return Number(user.role_id) === 1 || Number(user.user_role_id) === 1;
}

export function getUserRoleId(user) {
  return Number(user?.role_id ?? user?.user_role_id ?? 0);
}

/** Client role — no assessment module or menus */
export function isAssessmentBlocked(user) {
  return getUserRoleId(user) === 2;
}

export function canAccessAssessment(user) {
  if (!user || isAssessmentBlocked(user)) return false;
  return isAssessmentAdmin(user) || isAssessmentStudent(user);
}

export function getPostLoginPath(user) {
  if (isAssessmentStudent(user)) return '/assessment';
  if (isAssessmentBlocked(user)) return '/tickets';
  return '/home';
}

/** DB/API may return 0, "0", false, "No" — avoid !! coercion treating "0" as true */
/** API/DB may return intro_videos as JSON string, array, or object — always coerce to URL strings[] */
export function normalizeIntroVideosList(raw) {
  if (raw == null || raw === '') return [];
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      const trimmed = raw.trim();
      parsed = trimmed ? [trimmed] : [];
    }
  }
  let items = [];
  if (Array.isArray(parsed)) {
    items = parsed
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          return String(item.url || item.path || item.src || item.file || '').trim();
        }
        return '';
      });
  } else if (parsed && typeof parsed === 'object') {
    items = Object.values(parsed)
      .map((v) => (typeof v === 'string' ? v.trim() : ''));
  }
  return items
    .filter(Boolean)
    .filter((v) => v.includes('/') || v.includes('\\') || /^https?:\/\//i.test(v));
}

/** Asset base for intro files (same as testService.getImageBaseURL / VideoModal). */
export function resolveIntroVideoAssetBase(apiBaseURL) {
  const imageBase = resolveAssessmentImageBaseUrl();
  const apiBase = String(apiBaseURL || '')
    .replace(/\/api\/?$/i, '')
    .replace(/\/$/, '');
  return String(imageBase || apiBase || '').replace(/\/$/, '');
}

/** Primary playable URL — matches VideoModal: `${baseURL}${path}`. */
export function buildIntroVideoPlayUrl(url, apiBaseURL) {
  if (url == null || url === '') return '';
  const u = String(url).trim();
  if (/^https?:\/\//i.test(u)) return u;

  const base = resolveIntroVideoAssetBase(apiBaseURL);
  const path = u.startsWith('/') ? u : `/${u}`;
  if (!base) return path;

  return `${base}${path}`;
}

/**
 * Candidate URLs to try when the primary path 404s (some Apache/CI4 installs expose uploads under /public/).
 * @returns {string[]}
 */
export function getIntroVideoPlayUrlCandidates(url, apiBaseURL) {
  const primary = buildIntroVideoPlayUrl(url, apiBaseURL);
  if (!primary) return [];

  const u = String(url).trim();
  if (/^https?:\/\//i.test(u)) return [primary];

  const base = resolveIntroVideoAssetBase(apiBaseURL);
  const pathNoSlash = u.replace(/^\//, '');
  const candidates = [primary];

  if (
    base &&
    pathNoSlash.startsWith('uploads/') &&
    !/\/public$/i.test(base) &&
    !base.includes('/public/')
  ) {
    const withPublic = `${base}/public/${pathNoSlash}`;
    if (withPublic !== primary) {
      candidates.push(withPublic);
    }
  }

  return candidates;
}

/** @deprecated Use buildIntroVideoPlayUrl — kept for existing imports. */
export function resolveIntroVideoUrl(url, apiBaseURL) {
  return buildIntroVideoPlayUrl(url, apiBaseURL);
}

/** API/DB may return submission_files as JSON string or array. */
export function normalizeSubmissionFilesList(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      const trimmed = raw.trim();
      return trimmed ? [trimmed] : [];
    }
  }
  return [];
}

/** Resolve stored submission file path to absolute URL (same base as intro videos). */
export function resolveSubmissionFileUrl(url, apiBaseURL) {
  return buildIntroVideoPlayUrl(url, apiBaseURL);
}

export function getSubmissionFileLabel(url) {
  if (!url) return 'File';
  const path = String(url).split(/[?#]/)[0];
  const name = path.split('/').pop();
  return name || 'File';
}

export function getIntroMaterialKind(url) {
  if (!url) return 'unknown';
  try {
    const path = String(url).split(/[?#]/)[0].toLowerCase();
    const ext = path.split('.').pop();
    if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'mpeg', 'mpg', 'ogv', 'm4v'].includes(ext)) return 'video';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    return 'other';
  } catch {
    return 'unknown';
  }
}

export function isAssessmentFlagOn(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value === null || value === undefined) return false;
  const s = String(value).trim().toLowerCase();
  if (s === '' || s === '0' || s === 'no' || s === 'false' || s === 'off') return false;
  if (s === '1' || s === 'yes' || s === 'true' || s === 'on') return true;
  return false;
}